import { useState, useEffect } from 'react';
import { API_ENDPOINTS, fetchFromApi } from '../config/api';
import { FaInstagram } from 'react-icons/fa';
import '../styles/Connect.css';

const Connect = () => {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [tipLink, setTipLink] = useState('');
  const [artists, setArtists] = useState([]);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        const response = await fetchFromApi('/api/check-inquiries-permission');
        setHasPermission(response.has_permission);
      } catch (err) {
        console.error('Error checking permissions:', err);
        setHasPermission(false);
      }
    };
    checkPermission();
  }, []);

  // Fetch tip link from API
  useEffect(() => {
    const fetchTipLink = async () => {
      try {
        const response = await fetchFromApi(`${API_ENDPOINTS.main}?section=tip`);
        if (response && response.contents) {
          setTipLink(response.contents);
          console.log('Tip link fetched:', response.contents);
        }
      } catch (error) {
        console.error('Error fetching tip link:', error);
      }
    };
    
    fetchTipLink();
  }, []);
  
  // Fetch artists for booking section
  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const response = await fetchFromApi(API_ENDPOINTS.artists);
        if (response && Array.isArray(response)) {
          const artistsWithInstagram = response.filter(artist => 
            artist.social_links && artist.social_links.instagram
          );
          setArtists(artistsWithInstagram);
        }
      } catch (error) {
        console.error('Error fetching artists:', error);
      }
    };
    
    fetchArtists();
  }, []);

  // If no permission, don't render the form
  if (!hasPermission) {
    return null;
  }

  const handleChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetchFromApi(API_ENDPOINTS.inquiries, {
        method: 'POST',
        body: JSON.stringify({ email: email })
      });
      
      if (response.message && response.inquiry) {
        setEmail('');
        setSubmitStatus('success');
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  if (submitStatus === 'error') {
    return (
      <section className="connect-section" id="connect">
        <div className="connect-container">
          <h2>Connect</h2>
          <div className="status-message error">
            Sorry, the form appears to be down, please try again.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="connect" className="connect-section">
      <div className="connect-container">
        <h3>Stay Connected</h3>
        <p className="connect-description">
          Building our community outside of the platforms
        </p>
        {submitStatus === 'success' && (
          <div className="status-message success">
            Thank you for joining our growing community.
          </div>
        )}

        <form className="connect-form" onSubmit={handleSubmit}>
          <div className="form-group full-width">
            <input
              type="email"
              value={email}
              onChange={handleChange}
              placeholder="Your Email"
              required
              disabled={submitting}
              autoComplete="off"
            />
            <br />
            <button 
              type="submit" 
              className={`submit-button ${submitting ? 'loading' : ''}`}
              disabled={submitting}
            >
              {submitting ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
        
        {tipLink && (
          <div className="tip-button-container">
            <h3>Support the Artists</h3>
            <p className="connect-description">
              Directly support our artists
            </p>
            <a 
              href={tipLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="tip-button"
            >
              Support
            </a>
          </div>
        )}
        
        <div className="booking-section">
          <h3>Communications</h3>
          <p className="connect-description">
            DM for booking, collaboration, and conversation, we love to hear from you!
          </p>
          <div className="artist-social-grid">
            {/* The Lost Loft main Instagram */}
            <a 
              href="https://www.instagram.com/thelostloftchicago"
              target="_blank"
              rel="noopener noreferrer"
              className="artist-social-link"
            >
              <FaInstagram className="instagram-icon" />
              <span>The Lost Loft</span>
            </a>
            
            {/* Artist Instagrams */}
            {artists.map((artist, index) => (
              <a 
                key={index}
                href={artist.social_links.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="artist-social-link"
              >
                <FaInstagram className="instagram-icon" />
                <span>{artist.name}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Connect;
